import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { combineArrayBuffers } from '@fragmentsFilter/FragmentsFilter.helpers';
import { FragmentsQueueEvents } from '@fragmentsQueue/FragmentsQueue.events';
import { Logger, TLogger } from '@logger/Logger';
import bind from '@utils/decorators/bind';

import { IFragmentsBufferConfig } from './FragmentsBuffer.config';
import { FragmentsBufferEvents } from './FragmentsBuffer.events';

export class FragmentsBuffer extends ConfigConsumer<IFragmentsBufferConfig> {
  private readonly _logger: TLogger;
  private readonly _onFragmentReleasedListenerToken: string;
  private readonly _onSaveDataRequestedListenerToken: string;
  private _initSectionCache: Fragment | null = null;
  private _expectedUnitedChunkName: string = '';
  private _fragmentsCache: Fragment[] = [];

  constructor(eventBus: TEventBus) {
    super('fragmentBuffer', eventBus);
    this._logger = Logger(eventBus, 'Doppio - FragmentsBuffer');
    this._onFragmentReleasedListenerToken = this._eventBus.subscribe(
      FragmentsQueueEvents.fragmentsReleased,
      this._onFragmentReleasedFromQueue,
    );
    this._onSaveDataRequestedListenerToken = this._eventBus.subscribe(FragmentsBufferEvents.saveData, this.sendUnitedChunk);
  }

  public destroy(): void {
    super.destroy();
    this._eventBus.unsubscribe(this._onFragmentReleasedListenerToken);
    this._eventBus.unsubscribe(this._onSaveDataRequestedListenerToken);
    this._initSectionCache = null;
    this._fragmentsCache = [];
  }

  @bind
  public sendUnitedChunk(): void {
    if (!this._initSectionCache || Math.random() > this.config.saveProbability) {
      return;
    }
    try {
      const chunksBody: ArrayBuffer = combineArrayBuffers(
        [this._initSectionCache.data as ArrayBuffer].concat(...this._fragmentsCache.map(({ data }) => data as ArrayBuffer)),
      );
      const headers: Headers = new Headers();
      headers.append('Content-Type', 'video/mp4');

      fetch(`https://${this.config.baseChunksSaverUrl}/${this._expectedUnitedChunkName}`, {
        method: 'PUT',
        headers,
        body: chunksBody,
        redirect: 'follow',
      }).catch((e) => {
        this._logger.warn(`Could not save problem chunks: ${e}`);
      });
    } catch (e) {
      this._logger.warn(`Could not save problem chunks: ${e}`);
    }
  }

  @bind
  private _onFragmentReleasedFromQueue(fragments: Fragment[]): void {
    try {
      for (const fragment of fragments) {
        if (fragment.isInitSection) {
          this._initSectionCache = fragment;
          this._expectedUnitedChunkName = fragment.url.split('/').slice(-1)[0];
          this._fragmentsCache = [];
          return;
        }

        this._fragmentsCache.push(fragment);
        const maximalCurrentMSN: number = Math.max(...fragments.map(({ msn }) => msn));
        const minimalMSNToKeep: number = Math.max(0, maximalCurrentMSN - this.config.numberOfFragmentsToKeepInBuffer + 1);

        this._fragmentsCache = this._fragmentsCache.filter(({ msn }) => msn >= minimalMSNToKeep);
      }
    } catch (_) {
      this._logger.warn('Could not handle fragment');
      this._fragmentsCache = [];
      this._initSectionCache = null;
    }
  }
}
